import React from 'react';
import { Car, Key, Users, CalendarDays, Navigation, UserCheck } from 'lucide-react';
import { formatImgUrl } from '../../utils/imageUtils';
import './Dashboard.css';

const CarDashboard = ({ entityData, bookings }) => {
    const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const coverPageUrl = entityData?.cover_page ? formatImgUrl(entityData.cover_page) : null;
    const displayName = entityData?.name || cachedUser.name || "Car Rental Partner";
    const address = entityData?.address || "Local Area";
    const statusLabel = entityData?.status || "ACTIVE";

    // Compute KPIs
    const todayStr = new Date().toISOString().split('T')[0];
    const bookingsToday = (bookings || []).filter(b => b.created_at && String(b.created_at).startsWith(todayStr)).length;
    const upcomingPickups = (bookings || []).filter(b => b.status === 'Confirmed' && new Date(b.check_in) >= new Date()).length;
    const activeRentals = (bookings || []).filter(b => b.status === 'Pending' || b.status === 'Confirmed' || b.status === 'Checked In').length;

    // Last 7 days bookings count
    const last7DaysTotal = (bookings || []).filter(b => {
        const bDate = new Date(b.created_at);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return bDate >= sevenDaysAgo;
    }).length;

    // Real Preparation Tasks
    const preparationTasks = (bookings || [])
        .filter(b => {
            const checkIn = new Date(b.check_in).toISOString().split('T')[0];
            const checkOut = new Date(b.check_out).toISOString().split('T')[0];
            return checkIn === todayStr || checkOut === todayStr;
        })
        .slice(0, 4)
        .map(b => {
            const isPickup = new Date(b.check_in).toISOString().split('T')[0] === todayStr;
            return {
                id: b.id,
                car: b.entity_name || "Assigned Car",
                type: isPickup ? "Pick-up" : "Return",
                guest: b.guest_name || "N/A",
                time: isPickup ? "10:00 AM" : "12:00 PM",
                status: b.payment_status === 'Paid' ? "READY" : "PENDING",
                icon: isPickup ? <Key size={20} /> : <UserCheck size={20} />,
                color: isPickup ? "green" : "blue"
            };
        });

    return (
        <main className="main-content">
            {/* Hero Section */}
            <div className="hero-section">
                <div className="hero-card" style={{ background: "linear-gradient(135deg, #0288AC 0%, #04252E 100%)", position: "relative" }}>
                    {coverPageUrl && (
                        <img 
                            src={coverPageUrl} 
                            alt="Cover" 
                            className="hero-bg" 
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                    )}
                    <div className="profile-overlay-outer">
                        <div className="hotel-profile-card">
                            <div className="hotel-logo-box" style={{ background: "#0288AC" }}>
                                <Car size={32} color="white" />
                            </div>
                            <div className="hotel-details">
                                <h1>{displayName}</h1>
                                <p>{address}</p>
                                <div className="status-badge-mini">
                                    <div className="dot"></div>
                                    {statusLabel.toUpperCase()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Info Badges */}
            <div className="info-grid">
                <div className="info-item">
                    <div className="info-icon"><Car size={20} /></div>
                    <div>
                        <p className="info-label">FLEET SIZE</p>
                        <p className="info-value">1 Vehicle Managed</p>
                    </div>
                </div>
                <div className="info-item">
                    <div className="info-icon"><Navigation size={20} /></div>
                    <div>
                        <p className="info-label">PRIMARY LOCATION</p>
                        <p className="info-value">{address}</p>
                    </div>
                </div>
                <div className="info-item">
                    <div className="info-icon"><Users size={20} /></div>
                    <div>
                        <p className="info-label">TOTAL CUSTOMERS</p>
                        <p className="info-value">{(bookings || []).length} Renters</p>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
                <div className="stat-card border-blue">
                    <p className="stat-label">Bookings Today</p>
                    <p className="stat-value">{bookingsToday} <span className="stat-trend">Live</span></p>
                </div>
                <div className="stat-card border-blue">
                    <p className="stat-label">Upcoming Pickups</p>
                    <p className="stat-value">{upcomingPickups}</p>
                </div>
                <div className="stat-card border-blue">
                    <p className="stat-label">Active Rentals</p>
                    <p className="stat-value">{activeRentals} <span className="stat-sub">Pending Review</span></p>
                </div>
                <div className="stat-card border-blue">
                    <p className="stat-label">Maintenance / Issues</p>
                    <p className="stat-value">0</p>
                </div>
            </div>

            {/* Bottom Section */}
            <div className="bottom-section">
                <div className="preparation-section">
                    <div className="section-header">
                        <h2>Today's Fleet Schedule</h2>
                        <button className="view-all">View All</button>
                    </div>
                    <div className="task-list">
                        {preparationTasks.length > 0 ? (
                            preparationTasks.map(task => (
                                <div className="task-item-container" key={task.id}>
                                    <div className="task-item">
                                        <div className={`task-icon-container icon-${task.color}`}>
                                            {task.icon}
                                        </div>
                                        <div className="task-info">
                                            <p className="task-title">{task.car} - {task.type}</p>
                                            <p className="task-subtitle">{task.guest ? `Customer: ${task.guest} • ${task.time}` : `Status: ${task.status}`}</p>
                                        </div>
                                        {task.status && <span className={`status-pill pill-${task.color}`}>{task.status}</span>}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-tasks">No pickups or returns scheduled for today.</div>
                        )}
                    </div>
                </div>

                <div className="trend-section">
                    <div className="section-header">
                        <h2>Orders Trend</h2>
                        <p className="section-sub">Last 7 days performance</p>
                    </div>
                    <div className="trend-content">
                        <div className="trend-placeholder">
                            <CalendarDays size={48} color="#e5e7eb" />
                            <div className="days-label">
                                <span>MON</span><span>TUE</span><span>WED</span><span>THU</span><span>FRI</span><span className="active">SAT</span><span>SUN</span>
                            </div>
                        </div>
                        <div className="trend-footer">
                            <p>Weekly Total</p>
                            <h3>{last7DaysTotal} Bookings</h3>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default CarDashboard;
